import type { SupabaseClient, User } from '@supabase/supabase-js';

/**
 * Ensure the public.users row exists and the avatar is current for the given
 * auth user.  The trigger `on_auth_user_created` handles initial creation at
 * sign-up but may miss users created before the trigger existed or via methods
 * that bypass it.
 *
 * 1. Upsert with `ignoreDuplicates` so existing rows are not overwritten
 *    (protects user-edited profile data).
 * 2. Conditionally update `avatar_url` via a second write — this is the only
 *    field kept in sync with the OAuth provider on every login.
 */
export async function ensureUserRow(sb: SupabaseClient, user: User): Promise<void> {
  const avatarUrl = user.user_metadata?.avatar_url ?? user.user_metadata?.picture ?? null;

  const userRow = {
    id: user.id,
    name: user.user_metadata?.full_name ?? user.user_metadata?.name ?? user.email ?? '',
    ...(avatarUrl ? { avatar_url: avatarUrl } : {}),
  };

  const { error } = await sb
    .from('users')
    .upsert(userRow, { onConflict: 'id', ignoreDuplicates: true });

  if (error) throw error;

  if (avatarUrl) {
    const { error: avatarError } = await sb
      .from('users')
      .update({ avatar_url: avatarUrl })
      .eq('id', user.id);

    if (avatarError) throw avatarError;
  }
}
